
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Accordion } from 'react-bootstrap';
import { toast } from 'react-toastify';

const ICON_OPTIONS = [
  'ri-shield-star-line', 'ri-heart-line', 'ri-global-line', 'ri-leaf-line',
  'ri-award-line', 'ri-team-line', 'ri-map-pin-line', 'ri-flight-takeoff-line',
  'ri-hotel-line', 'ri-customer-service-line', 'ri-thumb-up-line', 'ri-star-line',
  'ri-trophy-line', 'ri-handshake-line', 'ri-lightbulb-line', 'ri-rocket-line',
  'ri-check-double-line', 'ri-time-line', 'ri-money-dollar-circle-line', 'ri-phone-line',
];

const emptySection = () => ({ label: '', title: '', description: '', image: '' });
const emptyStat    = () => ({ v: '', l: '' });
const emptyValue   = () => ({ icon: 'ri-star-line', t: '', d: '' });
const emptyTeam    = () => ({ name: '', role: '', initials: '', image: '' });
const emptyFeature = () => ({ icon: 'ri-check-double-line', title: '', description: '' });
const emptyGallery = () => ({ url: '', caption: '' });

// ── ALL HELPER COMPONENTS DEFINED OUTSIDE AboutUsEditor ─────────────────────
// This is critical: defining them inside causes React to treat them as new
// component types on every render, which unmounts/remounts DOM nodes and
// steals focus from whichever input the user is typing in.

const SectionHeader = ({ label }) => (
  <h6 className="text-primary fw-bold text-uppercase small mb-4" style={{ letterSpacing: '1px' }}>
    {label}
  </h6>
);

const FieldRow = ({ label, children }) => (
  <div className="mb-3">
    <Form.Label className="small fw-bold">{label}</Form.Label>
    {children}
  </div>
);

const AddBtn = ({ onClick, label = 'Add' }) => (
  <Button
    variant="outline-primary"
    size="sm"
    className="mt-2 fw-bold d-flex align-items-center gap-2"
    onClick={onClick}
  >
    <i className="ri-add-circle-line" /> {label}
  </Button>
);

const RemoveBtn = ({ onClick }) => (
  <Button
    variant="outline-danger"
    size="sm"
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    style={{ padding: '2px 8px' }}
  >
    <i className="ri-delete-bin-line" />
  </Button>
);
const IconPicker = ({ value, onChange }) => (
  <Form.Select size="sm" value={value} onChange={e => onChange(e.target.value)}>
    {ICON_OPTIONS.map(ic => (
      <option key={ic} value={ic}>
        {ic.replace('ri-', '').replace(/-line$/, '').replace(/-/g, ' ')}
      </option>
    ))}
  </Form.Select>
);

const ItemCard = ({ children, onRemove }) => (
  <div className="border rounded p-3 pt-4 mb-3 bg-light position-relative">
    <div style={{ position: 'absolute', top: '8px', right: '10px', zIndex: 10 }}>
      <RemoveBtn onClick={onRemove} />
    </div>
    {children}
  </div>
);

// ── MAIN COMPONENT ───────────────────────────────────────────────────────────

const AboutUsEditor = ({ workingData, updateSection, onSave, isSaving }) => {

  // ── PURE LOCAL STATE — never driven from parent after mount ─────────────
  // We push to parent ONLY when Save is clicked.
  const [ab, setAb] = useState(() => ({
    heroTitle:     '',
    heroSubtitle:  '',
    heroBgImage:   '',
    storyLabel:    'OUR STORY',
    title:         '',
    description:   '',
    description2:  '',
    image:         '',
    stats: [
      { v: '10+',  l: 'Years Experience' },
      { v: '50K+', l: 'Happy Travelers'  },
      { v: '100+', l: 'Destinations'     },
      { v: '24/7', l: 'Support'          },
    ],
    valuesLabel:   'OUR VALUES',
    valuesTitle:   'What We Stand For',
    values: [
      { icon: 'ri-shield-star-line', t: 'Trust & Transparency', d: 'Honest pricing with no hidden fees.' },
      { icon: 'ri-heart-line',       t: 'Customer First',        d: 'Every decision centers on our travelers.' },
    ],
    featuresLabel: 'WHAT WE OFFER',
    featuresTitle: 'Everything You Need for a Perfect Trip',
    features: [
      { icon: 'ri-flight-takeoff-line',     title: 'Flight Booking', description: 'Best fares across all major airlines.' },
      { icon: 'ri-hotel-line',              title: 'Hotel Deals',    description: 'Handpicked stays at every budget.'      },
      { icon: 'ri-map-pin-line',            title: 'Tour Packages',  description: 'Curated itineraries worldwide.'         },
      { icon: 'ri-customer-service-2-line', title: '24/7 Support',   description: 'We are here whenever you need us.'     },
    ],
    teamLabel:     'OUR TEAM',
    teamTitle:     'Meet the Experts Behind Your Journey',
    team: [
      { name: 'Travel Expert', role: 'Flights Specialist',   initials: 'TE', image: '' },
      { name: 'Hotel Curator', role: 'Accommodation Expert', initials: 'HC', image: '' },
    ],
    gallery:       [],
    extraSections: [],
    ctaTitle:      'Ready to Start Your Journey?',
    ctaSubtitle:   'Let us help you plan the perfect trip tailored to your needs.',
    ctaButtonText: 'Explore Now',
    ctaButtonLink: '/',
    // DB data wins over defaults
    ...workingData.aboutUs,
  }));

  // ── simple local setters — NO parent call here ───────────────────────────
  const set = (key, val) => setAb(prev => ({ ...prev, [key]: val }));

  const addItem    = (key, empty)           => setAb(prev => ({ ...prev, [key]: [...(prev[key] || []), empty()] }));
  const removeItem = (key, idx)             => setAb(prev => ({ ...prev, [key]: prev[key].filter((_, i) => i !== idx) }));
  const updateItem = (key, idx, field, val) => setAb(prev => {
    const arr = [...(prev[key] || [])];
    arr[idx] = { ...arr[idx], [field]: val };
    return { ...prev, [key]: arr };
  });
const handleSave = async () => {
  updateSection('aboutUs', ab);        // keep parent in sync for UI
  if (onSave) await onSave(ab);        // ← pass ab directly
  toast.success('About Us saved successfully!');
};
  useEffect(() => {
    return () => {
    };
  }, []);

  return (
    <Container className="py-4">
      <Row className="justify-content-center">
        <Col lg={11}>
          <Card className="shadow-sm border-0 rounded-4">

            <Card.Header className="bg-white py-3 border-bottom">
              <h5 className="fw-bold mb-0">
                <i className="ri-file-info-line me-2 text-primary" />
                About Us — Content Editor
              </h5>
            </Card.Header>

            <Card.Body className="p-0">
              <Accordion flush>

                {/* ── 1. HERO ─────────────────────────────────── */}
                <Accordion.Item eventKey="0">
                  <Accordion.Header>
                    <i className="ri-image-2-line me-2 text-primary" />
                    <strong>Hero Section</strong>
                    <span className="ms-2 text-muted small">— Page banner title &amp; background</span>
                  </Accordion.Header>
                  <Accordion.Body>
                    <SectionHeader label="1. Hero Banner" />
                    <Row className="g-3">
                      <Col md={6}>
                        <FieldRow label="Hero Title">
                          <Form.Control
                            value={ab.heroTitle}
                            placeholder={`About ${workingData.branding?.companyName || 'Your Company'}`}
                            onChange={e => set('heroTitle', e.target.value)}
                          />
                        </FieldRow>
                      </Col>
                      <Col md={6}>
                        <FieldRow label="Hero Subtitle">
                          <Form.Control
                            value={ab.heroSubtitle}
                            placeholder="Your trusted travel partner..."
                            onChange={e => set('heroSubtitle', e.target.value)}
                          />
                        </FieldRow>
                      </Col>
                      <Col md={12}>
                        <FieldRow label="Background Image URL">
                          <Form.Control
                            value={ab.heroBgImage}
                            placeholder="https://images.unsplash.com/..."
                            onChange={e => set('heroBgImage', e.target.value)}
                          />
                          {ab.heroBgImage && (
                            <img
                              src={ab.heroBgImage}
                              alt="preview"
                              className="mt-2 rounded"
                              style={{ width: '100%', maxHeight: '120px', objectFit: 'cover', opacity: 0.85 }}
                              onError={e => e.target.style.display = 'none'}
                            />
                          )}
                        </FieldRow>
                      </Col>
                    </Row>
                  </Accordion.Body>
                </Accordion.Item>

                {/* ── 2. OUR STORY ────────────────────────────── */}
                <Accordion.Item eventKey="1">
                  <Accordion.Header>
                    <i className="ri-book-open-line me-2 text-primary" />
                    <strong>Our Story</strong>
                    <span className="ms-2 text-muted small">— Company history &amp; image</span>
                  </Accordion.Header>
                  <Accordion.Body>
                    <SectionHeader label="2. Our Story" />
                    <Row className="g-3">
                      <Col md={4}>
                        <FieldRow label="Section Label (small caps)">
                          <Form.Control
                            value={ab.storyLabel}
                            placeholder="OUR STORY"
                            onChange={e => set('storyLabel', e.target.value)}
                          />
                        </FieldRow>
                      </Col>
                      <Col md={8}>
                        <FieldRow label="Section Heading">
                          <Form.Control
                            value={ab.title}
                            placeholder="Turning Dreams into Destinations Since Day One"
                            onChange={e => set('title', e.target.value)}
                          />
                        </FieldRow>
                      </Col>
                      <Col md={12}>
                        <FieldRow label="Main Description">
                          <Form.Control
                            as="textarea"
                            rows={3}
                            value={ab.description}
                            placeholder="Founded with a passion for travel..."
                            onChange={e => set('description', e.target.value)}
                          />
                        </FieldRow>
                      </Col>
                      <Col md={12}>
                        <FieldRow label="Second Paragraph (optional)">
                          <Form.Control
                            as="textarea"
                            rows={2}
                            value={ab.description2}
                            placeholder="From budget-friendly getaways to luxury escapes..."
                            onChange={e => set('description2', e.target.value)}
                          />
                        </FieldRow>
                      </Col>
                      <Col md={12}>
                        <FieldRow label="Story Image URL (shown beside text)">
                          <Form.Control
                            value={ab.image}
                            placeholder="https://images.pexels.com/..."
                            onChange={e => set('image', e.target.value)}
                          />
                          {ab.image && (
                            <img
                              src={ab.image}
                              alt="story"
                              className="mt-2 rounded"
                              style={{ width: '100%', maxHeight: '150px', objectFit: 'cover' }}
                              onError={e => e.target.style.display = 'none'}
                            />
                          )}
                        </FieldRow>
                      </Col>
                    </Row>
                  </Accordion.Body>
                </Accordion.Item>

                {/* ── 3. STATS ────────────────────────────────── */}
                <Accordion.Item eventKey="2">
                  <Accordion.Header>
                    <i className="ri-bar-chart-line me-2 text-primary" />
                    <strong>Stats / Numbers</strong>
                    <span className="ms-2 text-muted small">— 10+ Years, 50K+ Travelers etc.</span>
                  </Accordion.Header>
                  <Accordion.Body>
                    <SectionHeader label="3. Stats" />
                    {(ab.stats || []).map((s, i) => (
                      <ItemCard key={i} onRemove={() => removeItem('stats', i)}>
                        <Row className="g-2">
                          <Col xs={4}>
                            <Form.Label className="small fw-bold">Value</Form.Label>
                            <Form.Control
                              size="sm"
                              value={s.v}
                              placeholder="10+"
                              onChange={e => updateItem('stats', i, 'v', e.target.value)}
                            />
                          </Col>
                          <Col xs={8}>
                            <Form.Label className="small fw-bold">Label</Form.Label>
                            <Form.Control
                              size="sm"
                              value={s.l}
                              placeholder="Years Experience"
                              onChange={e => updateItem('stats', i, 'l', e.target.value)}
                            />
                          </Col>
                        </Row>
                      </ItemCard>
                    ))}
                    <AddBtn onClick={() => addItem('stats', emptyStat)} label="Add Stat" />
                  </Accordion.Body>
                </Accordion.Item>

                {/* ── 4. VALUES ───────────────────────────────── */}
                <Accordion.Item eventKey="3">
                  <Accordion.Header>
                    <i className="ri-heart-line me-2 text-primary" />
                    <strong>Our Values</strong>
                    <span className="ms-2 text-muted small">— Icon cards with title &amp; description</span>
                  </Accordion.Header>
                  <Accordion.Body>
                    <SectionHeader label="4. Values" />
                    <Row className="g-3 mb-3">
                      <Col md={4}>
                        <FieldRow label="Section Label">
                          <Form.Control
                            value={ab.valuesLabel}
                            placeholder="OUR VALUES"
                            onChange={e => set('valuesLabel', e.target.value)}
                          />
                        </FieldRow>
                      </Col>
                      <Col md={8}>
                        <FieldRow label="Section Heading">
                          <Form.Control
                            value={ab.valuesTitle}
                            placeholder="What We Stand For"
                            onChange={e => set('valuesTitle', e.target.value)}
                          />
                        </FieldRow>
                      </Col>
                    </Row>
                    {(ab.values || []).map((v, i) => (
                      <ItemCard key={i} onRemove={() => removeItem('values', i)}>
                        <Row className="g-2">
                          <Col md={4}>
                            <Form.Label className="small fw-bold">Icon</Form.Label>
                            <div className="d-flex align-items-center gap-2">
                              <i className={v.icon} style={{ fontSize: '20px', color: '#0d6efd' }} />
                              <IconPicker value={v.icon} onChange={val => updateItem('values', i, 'icon', val)} />
                            </div>
                          </Col>
                          <Col md={4}>
                            <Form.Label className="small fw-bold">Title</Form.Label>
                            <Form.Control
                              size="sm"
                              value={v.t}
                              placeholder="Trust & Transparency"
                              onChange={e => updateItem('values', i, 't', e.target.value)}
                            />
                          </Col>
                          <Col md={4}>
                            <Form.Label className="small fw-bold">Description</Form.Label>
                            <Form.Control
                              size="sm"
                              as="textarea"
                              rows={2}
                              value={v.d}
                              placeholder="Short description..."
                              onChange={e => updateItem('values', i, 'd', e.target.value)}
                            />
                          </Col>
                        </Row>
                      </ItemCard>
                    ))}
                    <AddBtn onClick={() => addItem('values', emptyValue)} label="Add Value Card" />
                  </Accordion.Body>
                </Accordion.Item>

                {/* ── 5. FEATURES ─────────────────────────────── */}
                <Accordion.Item eventKey="4">
                  <Accordion.Header>
                    <i className="ri-apps-line me-2 text-primary" />
                    <strong>Features / What We Offer</strong>
                    <span className="ms-2 text-muted small">— Services &amp; offerings grid</span>
                  </Accordion.Header>
                  <Accordion.Body>
                    <SectionHeader label="5. Features" />
                    <Row className="g-3 mb-3">
                      <Col md={4}>
                        <FieldRow label="Section Label">
                          <Form.Control
                            value={ab.featuresLabel}
                            placeholder="WHAT WE OFFER"
                            onChange={e => set('featuresLabel', e.target.value)}
                          />
                        </FieldRow>
                      </Col>
                      <Col md={8}>
                        <FieldRow label="Section Heading">
                          <Form.Control
                            value={ab.featuresTitle}
                            placeholder="Everything You Need for a Perfect Trip"
                            onChange={e => set('featuresTitle', e.target.value)}
                          />
                        </FieldRow>
                      </Col>
                    </Row>
                    {(ab.features || []).map((f, i) => (
                      <ItemCard key={i} onRemove={() => removeItem('features', i)}>
                        <Row className="g-2">
                          <Col md={3}>
                            <Form.Label className="small fw-bold">Icon</Form.Label>
                            <div className="d-flex align-items-center gap-2">
                              <i className={f.icon} style={{ fontSize: '20px', color: '#0d6efd' }} />
                              <IconPicker value={f.icon} onChange={val => updateItem('features', i, 'icon', val)} />
                            </div>
                          </Col>
                          <Col md={4}>
                            <Form.Label className="small fw-bold">Feature Title</Form.Label>
                            <Form.Control
                              size="sm"
                              value={f.title}
                              placeholder="Flight Booking"
                              onChange={e => updateItem('features', i, 'title', e.target.value)}
                            />
                          </Col>
                          <Col md={5}>
                            <Form.Label className="small fw-bold">Short Description</Form.Label>
                            <Form.Control
                              size="sm"
                              as="textarea"
                              rows={2}
                              value={f.description}
                              placeholder="Best fares across all major airlines."
                              onChange={e => updateItem('features', i, 'description', e.target.value)}
                            />
                          </Col>
                        </Row>
                      </ItemCard>
                    ))}
                    <AddBtn onClick={() => addItem('features', emptyFeature)} label="Add Feature" />
                  </Accordion.Body>
                </Accordion.Item>

                {/* ── 6. TEAM ─────────────────────────────────── */}
                <Accordion.Item eventKey="5">
                  <Accordion.Header>
                    <i className="ri-team-line me-2 text-primary" />
                    <strong>Our Team</strong>
                    <span className="ms-2 text-muted small">— Team member cards</span>
                  </Accordion.Header>
                  <Accordion.Body>
                    <SectionHeader label="6. Team Members" />
                    <Row className="g-3 mb-3">
                      <Col md={4}>
                        <FieldRow label="Section Label">
                          <Form.Control
                            value={ab.teamLabel}
                            placeholder="OUR TEAM"
                            onChange={e => set('teamLabel', e.target.value)}
                          />
                        </FieldRow>
                      </Col>
                      <Col md={8}>
                        <FieldRow label="Section Heading">
                          <Form.Control
                            value={ab.teamTitle}
                            placeholder="Meet the Experts Behind Your Journey"
                            onChange={e => set('teamTitle', e.target.value)}
                          />
                        </FieldRow>
                      </Col>
                    </Row>
                    {(ab.team || []).map((m, i) => (
                      <ItemCard key={i} onRemove={() => removeItem('team', i)}>
                        <Row className="g-2 align-items-center">
                          <Col md={3}>
                            <Form.Label className="small fw-bold">Full Name</Form.Label>
                            <Form.Control
                              size="sm"
                              value={m.name}
                              placeholder="Rahul Sharma"
                              onChange={e => updateItem('team', i, 'name', e.target.value)}
                            />
                          </Col>
                          <Col md={3}>
                            <Form.Label className="small fw-bold">Role / Designation</Form.Label>
                            <Form.Control
                              size="sm"
                              value={m.role}
                              placeholder="Flights Specialist"
                              onChange={e => updateItem('team', i, 'role', e.target.value)}
                            />
                          </Col>
                          <Col md={2}>
                            <Form.Label className="small fw-bold">Initials</Form.Label>
                            <Form.Control
                              size="sm"
                              value={m.initials}
                              maxLength={3}
                              placeholder="RS"
                              onChange={e => updateItem('team', i, 'initials', e.target.value.toUpperCase())}
                            />
                          </Col>
                          <Col md={4}>
                            <Form.Label className="small fw-bold">Photo URL (optional)</Form.Label>
                            <Form.Control
                              size="sm"
                              value={m.image}
                              placeholder="https://..."
                              onChange={e => updateItem('team', i, 'image', e.target.value)}
                            />
                          </Col>
                        </Row>
                      </ItemCard>
                    ))}
                    <AddBtn onClick={() => addItem('team', emptyTeam)} label="Add Team Member" />
                  </Accordion.Body>
                </Accordion.Item>

                {/* ── 7. GALLERY ──────────────────────────────── */}
                <Accordion.Item eventKey="6">
                  <Accordion.Header>
                    <i className="ri-gallery-line me-2 text-primary" />
                    <strong>Photo Gallery</strong>
                    <span className="ms-2 text-muted small">— Office / destination images</span>
                  </Accordion.Header>
                  <Accordion.Body>
                    <SectionHeader label="7. Gallery" />
                    {(ab.gallery || []).map((g, i) => (
                      <ItemCard key={i} onRemove={() => removeItem('gallery', i)}>
                        <Row className="g-2 align-items-center">
                          <Col md={8}>
                            <Form.Label className="small fw-bold">Image URL</Form.Label>
                            <Form.Control
                              size="sm"
                              value={g.url}
                              placeholder="https://images.unsplash.com/..."
                              onChange={e => updateItem('gallery', i, 'url', e.target.value)}
                            />
                          </Col>
                          <Col md={4}>
                            <Form.Label className="small fw-bold">Caption (optional)</Form.Label>
                            <Form.Control
                              size="sm"
                              value={g.caption}
                              placeholder="Our Mumbai Office"
                              onChange={e => updateItem('gallery', i, 'caption', e.target.value)}
                            />
                          </Col>
                          {g.url && (
                            <Col md={12}>
                              <img
                                src={g.url}
                                alt={g.caption || 'gallery'}
                                className="rounded mt-1"
                                style={{ height: '80px', objectFit: 'cover', width: '100%' }}
                                onError={e => e.target.style.display = 'none'}
                              />
                            </Col>
                          )}
                        </Row>
                      </ItemCard>
                    ))}
                    <AddBtn onClick={() => addItem('gallery', emptyGallery)} label="Add Image" />
                  </Accordion.Body>
                </Accordion.Item>

                {/* ── 8. EXTRA SECTIONS ───────────────────────── */}
                <Accordion.Item eventKey="7">
                  <Accordion.Header>
                    <i className="ri-layout-grid-line me-2 text-primary" />
                    <strong>Extra Sections</strong>
                    <span className="ms-2 text-muted small">— Awards, certifications, milestones, etc.</span>
                  </Accordion.Header>
                  <Accordion.Body>
                    <SectionHeader label="8. Custom Sections" />
                    <p className="text-muted small mb-3">
                      Add any additional section your template supports — awards, certifications, partnerships, milestones, etc.
                    </p>
                    {(ab.extraSections || []).map((s, i) => (
                      <ItemCard key={i} onRemove={() => removeItem('extraSections', i)}>
                        <Row className="g-2">
                          <Col md={4}>
                            <Form.Label className="small fw-bold">Section Label (small caps)</Form.Label>
                            <Form.Control
                              size="sm"
                              value={s.label || ''}
                              placeholder="AWARDS & RECOGNITION"
                              onChange={e => updateItem('extraSections', i, 'label', e.target.value)}
                            />
                          </Col>
                          <Col md={8}>
                            <Form.Label className="small fw-bold">Section Heading</Form.Label>
                            <Form.Control
                              size="sm"
                              value={s.title || ''}
                              placeholder="Our Achievements"
                              onChange={e => updateItem('extraSections', i, 'title', e.target.value)}
                            />
                          </Col>
                          <Col md={12}>
                            <Form.Label className="small fw-bold">Content / Description</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={3}
                              value={s.description || ''}
                              placeholder="Write the content for this section..."
                              onChange={e => updateItem('extraSections', i, 'description', e.target.value)}
                            />
                          </Col>
                          <Col md={12}>
                            <Form.Label className="small fw-bold">Image URL (optional)</Form.Label>
                            <Form.Control
                              size="sm"
                              value={s.image || ''}
                              placeholder="https://..."
                              onChange={e => updateItem('extraSections', i, 'image', e.target.value)}
                            />
                          </Col>
                        </Row>
                      </ItemCard>
                    ))}
                    <AddBtn onClick={() => addItem('extraSections', emptySection)} label="+ Add Section" />
                  </Accordion.Body>
                </Accordion.Item>

                {/* ── 9. CTA ──────────────────────────────────── */}
                <Accordion.Item eventKey="8">
                  <Accordion.Header>
                    <i className="ri-megaphone-line me-2 text-primary" />
                    <strong>Call to Action</strong>
                    <span className="ms-2 text-muted small">— Bottom CTA banner</span>
                  </Accordion.Header>
                  <Accordion.Body>
                    <SectionHeader label="9. CTA Banner" />
                    <Row className="g-3">
                      <Col md={6}>
                        <FieldRow label="CTA Heading">
                          <Form.Control
                            value={ab.ctaTitle}
                            placeholder="Ready to Start Your Journey?"
                            onChange={e => set('ctaTitle', e.target.value)}
                          />
                        </FieldRow>
                      </Col>
                      <Col md={6}>
                        <FieldRow label="CTA Subtitle">
                          <Form.Control
                            value={ab.ctaSubtitle}
                            placeholder="Let us help you plan the perfect trip..."
                            onChange={e => set('ctaSubtitle', e.target.value)}
                          />
                        </FieldRow>
                      </Col>
                      <Col md={6}>
                        <FieldRow label="Button Text">
                          <Form.Control
                            value={ab.ctaButtonText}
                            placeholder="Explore Now"
                            onChange={e => set('ctaButtonText', e.target.value)}
                          />
                        </FieldRow>
                      </Col>
                      <Col md={6}>
                        <FieldRow label="Button Link">
                          <Form.Control
                            value={ab.ctaButtonLink}
                            placeholder="/"
                            onChange={e => set('ctaButtonLink', e.target.value)}
                          />
                        </FieldRow>
                      </Col>
                    </Row>
                  </Accordion.Body>
                </Accordion.Item>

              </Accordion>
            </Card.Body>

            {/* ── SAVE FOOTER ── */}
            <Card.Footer className="bg-white border-top py-3">
              <div className="d-flex justify-content-between align-items-center">
                <small className="text-muted">
                  <i className="ri-information-line me-1 text-primary" />
                  Click Save to publish About Us content to the database.
                </small>
                <Button
                  variant="primary"
                  className="px-5 fw-bold shadow"
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'SAVE ABOUT US ✦'}
                </Button>
              </div>
            </Card.Footer>

          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default AboutUsEditor;